import {Injectable} from '@angular/core';
import {AppService} from '../../../app-common/services/app/app.service';
import {RequestsWebsocketService} from '../../../app-common/services/server/requests-websocket.service';
import {UserDto} from '../../data/userDto';
import {DomSanitizer, SafeStyle} from '@angular/platform-browser';
import {PleaseWaitService} from '../../../app-common/services/please-wait/please-wait.service';
import {EditAccountRequest} from '../../../auth/data/editAccountRequest';
import {QueryDTO, QueryFilter, QuerySort, QueryUnion} from '../../../query/data/queryCreator';
import {QueryableProperty} from '../../../query/data/queryableProperty';
import {ContainsValuePropertyFilter} from '../../../query/data/containsValuePropertyFilter';
import {ApiService} from '../../../app-common/services/server/api.service';
import {AuthService} from '../../../auth/services/auth/auth.service';
import {AppStateService, AppWindowState} from '../../../app-common/services/appState/app-state.service';
import {AddUserToTenantRequest} from '../../data/add-user-to-tenant.request';
import {LoggerService} from '../../../logging/services/logger.service';
import {LogLevel} from '../../../logging/data/logLevel';
import {LogSourceType} from '../../../logging/data/logSourceType';
import {LogSource} from '../../../logging/data/logSource';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private CURRENT_USER: UserDto;

  constructor(public appService: AppService,
              public appStateService: AppStateService,
              public pleaseWaitService: PleaseWaitService,
              public httpService: ApiService,
              private authService: AuthService,
              public websocket: RequestsWebsocketService,
              public sanitizer: DomSanitizer,
              private loggerService: LoggerService) {
    this.appService = appService;
    this.websocket = websocket;
    this.sanitizer = sanitizer;

    this.appStateService.appWindowStateChange.subscribe(async state => {
      if (state.previousValue === AppWindowState.Tenant && state.currentValue === AppWindowState.Loading) {
        await this.fetchCurrentUser();
      }
      if (state.currentValue === AppWindowState.Headless) {
        await this.fetchCurrentUser();
      }
    });
  }

  get currentUser(): UserDto {
    return this.CURRENT_USER;
  }

  get isAdmin(): boolean {
    return this.currentUser.groupId === 1;
  }

  getEmptyUser(): UserDto {
    const user = new UserDto();
    user.username = '';
    user.accountId = '';
    return user;
  }

  getEmptyAddUserToTenantRequest(): AddUserToTenantRequest {
    const request = new AddUserToTenantRequest();
    request.email = '';
    request.firstName = '';
    request.lastName = '';
    request.groupName = '';

    return request;
  }

  async addAccount(request: AddUserToTenantRequest): Promise<void> {
    request.email = request.email.trim();
    request.firstName = request.firstName.trim();
    request.lastName = request.lastName.trim();
    request.groupName = request.groupName.trim();

    this.pleaseWaitService.addToQueue();
    try {
      await this.httpService.tenantApiClient.postWithPD('TenantUser/CreateTenantUser', request);
      this.appService.showSuccessMessage('User Added');
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.UserService, {
        entity: request,
        message: 'Add account'
      });
    } catch (e) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.UserService, {
        entity: e,
        message: 'Add account'
      });
      throw e;
    } finally {
      this.pleaseWaitService.removeFromQueue();
    }
  }

  async editAccount(accountId: string, username: string, email: string, groupId: number): Promise<boolean> {
    username = username.trim();
    email = email.trim();

    const request = new EditAccountRequest();
    this.pleaseWaitService.addToQueue();
    request.username = username;
    request.email = email;
    request.groupId = groupId;

    try {
      await this.httpService.applicationApiClient.put('User', accountId, request);
      this.appService.showSuccessMessage('User Edited');
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.UserService, {
        entity: request,
        message: 'Edit account'
      });
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.UserService, {
        entity: ex,
        message: 'Edit account'
      });
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async toggleAccount(accountId: string, toggle: boolean): Promise<boolean> {
    this.pleaseWaitService.addToQueue();
    try {
      await this.httpService.applicationApiClient.patchWithCustomURL(`User/${accountId}/Toggle`, toggle);
      this.appService.showSuccessMessage(`User ${toggle ? 'Enabled' : 'Disabled'}`);
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.UserService, {
        entity: {
          accountId,
          toggle
        }, message: 'Toggle account'
      });
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.UserService, {
        entity:
        ex
        , message: 'Toggle account'
      });
      return false;
    }
  }

  async getUser(id: string): Promise<UserDto> {
    let response = await this.httpService.applicationApiClient.get<UserDto>(`User/${id}`);
    if (response) {
      response = this.trimUserDtoNames(response);
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.UserService, {
        entity: id,
        message: 'Get user'
      });
    }
    return response;
  }

  buildSearchQueryModel(query: string, entriesPerPage: number, pageNumber: number): QueryDTO {
    return QueryDTO.create()
      .withOffset(pageNumber)
      .withLimit(entriesPerPage)
      .withSort(new QuerySort().withField('creationDate').withDir('desc'))
      .withUnion(
        QueryUnion.create()
          .withFilter(QueryFilter.group(
            [
              QueryFilter.singleValue('username', 'contains', query),
            ],
          ))
      );
  }

  async search(username: string, entriesPerPage: number, pageNumber: number): Promise<UserDto[]> {
    const finalQueryDto = this.buildSearchQueryModel(username, entriesPerPage, pageNumber);
    const response = await this.httpService.applicationApiClient.getWithParams<UserDto[]>(`User/Search`, {filter: JSON.stringify(finalQueryDto)});
    if (response) {
      response.forEach(user => {
        user = this.trimUserDtoNames(user);
      });
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.UserService, {
        entity: {username, entriesPerPage, pageNumber},
        message: 'Get user'
      });
      return response;
    }
  }

  getUserColor(user: UserDto): SafeStyle {
    if (user === null || user === undefined || user.username === 'None') {
      return this.sanitizer.bypassSecurityTrustStyle('');
    } else {
      const myrng = Math.random();
      const hue = myrng * 360;

      const htmlStyle = `background: linear-gradient(165deg, hsl(${hue},100%,50%) 0%, hsl(${hue},100%,18%) 100%);`;
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.UserService, {
        entity: user,
        message: 'Get user color'
      });
      return this.sanitizer.bypassSecurityTrustStyle(htmlStyle);
    }
  }

  async fetchCurrentUser(): Promise<void> {
    const currentUser = await this.httpService.applicationApiClient.get<UserDto>(`User/my`);
    if (currentUser) {
      this.CURRENT_USER = currentUser;
      this.appStateService.appWindowState = AppWindowState.Main;
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.UserService, `Current User: ${this.CURRENT_USER.accountId}|${this.CURRENT_USER.username}`);
    }
  }

  delay(ms: number): Promise<void> {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.UserService, {
      entity: ms,
      message: 'Delay'
    });
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getAvatarType(account: UserDto): string {
    if (!account) {
      return '';
    }
    if (account.avatarUrl) {
      return 'image';
    }
    return 'initial';
  }

  getAvatarImage(account: UserDto): string {
    if (this.getAvatarType(account) === 'image') {
      return account.avatarUrl;
    } else {
      return '';
    }
  }

  getAvatarInitial(account: UserDto): string {
    if (this.getAvatarType(account) !== 'image') {
      const splits = account.username.split(' ');
      if (splits.length > 1) {
        return splits[0][0] + splits[1][0];
      } else {
        return account.username[0];
      }
    } else {
      return '';
    }
  }

  trimUserDtoNames(user: UserDto): UserDto {
    if (user) {
      if (user.username) {
        user.username = user.username.trim();
      }
    }
    return user;
  }

  private createContainsFilter(property: QueryableProperty, toSearch: any): ContainsValuePropertyFilter {
    const result = new ContainsValuePropertyFilter();
    result.active = true;
    result.property = property;
    result.queryValue = toSearch;
    return result;
  }
}
